import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { APP_CONFIG } from '@app/shared/config/app.config';
import {
  IConcentratorAccount,
  IProfitabilityResponse,
  IPurchaseRequest,
  IPurchaseResponse,
  IProfitabilityHistory,
} from '@app/shared/models/bouncer.model';
import { ICollection, IDtoResponse } from '@app/shared/models/dtos/response.dto';
import { CollectionParam } from '@app/shared/models/param.model';
import { filtersToParams } from '@app/shared/utils/filters-params';
import { map, Observable, of } from 'rxjs';
import { ServiceBase } from '../base/base.service';
import { IAccountAccumulated, IAccountBalance, IAccountGmf, IAccountLimits, IAccountBrebLimit } from '@app/shared/models/banking.model';
import { PATH } from '@app/shared/types/enums/path';
import { ActionEnum } from '@app/shared/types/enums/ActionEnum.enum';

@Injectable({
  providedIn: 'root',
})
export class BouncerService extends ServiceBase {
  constructor(public http: HttpClient) {
    super(`${APP_CONFIG.serviceBaseUrl}/banking/api/v1/bouncer`);
  }

  getConcentratorAccounts(concentratorAccountId: string): Observable<IConcentratorAccount[]> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<ICollection<IConcentratorAccount>>>(`${this.baseUrl}/flow/${concentratorAccountId}`, { headers })
      .pipe(map(result => result.data?.items || []));
  }

  makePurchase(rppUserId: string, accountCode: string, data: IPurchaseRequest): Observable<IDtoResponse<IPurchaseResponse>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.UPDATE);

    return this.http.put<IDtoResponse<IPurchaseResponse>>(`${this.baseUrl}/${rppUserId}/${accountCode}/purchase`, data, {
      headers,
    });
  }

  getProfitability(
    rppUserId: string,
    accountCode: string,
    startDate: string,
    endDate: string
  ): Observable<IProfitabilityResponse | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IProfitabilityResponse>>(
        `${this.baseUrl}/${rppUserId}/${accountCode}/profitability/resume?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      )
      .pipe(map(response => response.data));
  }

  getAccountBalance(accountCode: string): Observable<IAccountBalance | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IAccountBalance>>(`${this.baseUrl}/account/${accountCode}/balance`, { headers })
      .pipe(map(res => res.data));
  }

  getAccountAccumulated(accountCode: string): Observable<IAccountAccumulated | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IAccountAccumulated>>(`${this.baseUrl}/account/${accountCode}/accumulated?periodicity=DAILY&type=CREDIT`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  getAccountGmf(accountCode: string): Observable<IAccountGmf | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IAccountGmf>>(`${this.baseUrl}/account/${accountCode}/accumulated-gmf?tax_type=GMF&operation_type=DEBIT`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  getAccountBrebLimit(accountCode: string): Observable<IAccountBrebLimit | undefined> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);

    return this.http
      .get<IDtoResponse<IAccountBrebLimit>>(`${this.baseUrl}/account/${accountCode}/limit?operation=DEBIT&periodicity=DAILY`, {
        headers,
      })
      .pipe(map(res => res.data));
  }

  getProfitabilityHistory(
    rppUserId: string,
    accountCode: string,
    startDate: string,
    endDate: string,
    collectionParam: CollectionParam
  ): Observable<IDtoResponse<ICollection<IProfitabilityHistory>>> {
    const headers = new HttpHeaders().set('monitoring_view', PATH.ACCOUNT).set('monitoring_action', ActionEnum.READ);
    const filters = filtersToParams(collectionParam.filters || {});
    const params = new HttpParams({
      fromObject: {
        page_number: collectionParam.pageIndex,
        page_size: collectionParam.pageSize,
        ...filters,
      },
    });

    return this.http.get<IDtoResponse<ICollection<IProfitabilityHistory>>>(
      `${this.baseUrl}/${rppUserId}/${accountCode}/profitability/history?start_date=${startDate}&end_date=${endDate}`,
      {
        params,
        headers,
      }
    );
  }
}
